import { Autocomplete, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DynamicTable from "../../Components/Table/DynamicTable";
import { fetchProjects } from "../../Redux/WhatsappSlice/Project";
import {
  DeleteVersion,
  fetchVersions,
  updateVersion,
} from "../../Redux/WhatsappSlice/Version";
import Loader from "../../Components/Loader/Loader";

const ShowVersion = () => {
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear(); // Get the full 4-digit year
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const dispatch = useDispatch();

  const { VersionData } = useSelector((state) => state.Version);
  const { ProjectData } = useSelector((state) => state.Project);

  const [filters, setFilters] = useState({
    projectName: "",
    version: "",
  });

  useEffect(() => {
    dispatch(fetchVersions());
    dispatch(fetchProjects());
  }, [dispatch]);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);

  const versionOptions = [
    ...new Set(VersionData?.map((item) => item?.Version).filter(Boolean)),
  ];

  const handleDelete = async (row) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete version ${row.Version}?`
    );
    if (!isConfirmed) return;

    try {
      await dispatch(DeleteVersion({ id: row.MainId })).unwrap();
      toast.success("Version deleted successfully!", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
      dispatch(fetchVersions()); // refresh list
    } catch (error) {
      toast.error(`Failed to delete version: ${error}`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
    }
  };

  // const handleEditClick = (row) => {
  //   const matchedProject = ProjectData.find(
  //     (p) => p.ProjectName === row.Project
  //   );

  //   setCurrentVersion({
  //     _id: row.MainId,
  //     Project: matchedProject?._id || "",
  //     Version: row.Version,
  //     VersionDate: row.VersionDate
  //       ? new Date(row.VersionDate).toISOString().split("T")[0]
  //       : "",
  //   });

  //   setShowUpdateModal(true);
  // };

  const handleEditClick = (row) => {
    // Find the original, unformatted data from your Redux state
    const originalRowData = VersionData.find((item) => item._id === row.MainId);

    if (!originalRowData) {
      return;
    }

    const matchedProject = ProjectData.find(
      (p) => p.ProjectName === row.Project
    );

    setCurrentVersion({
      _id: row.MainId,
      Project: matchedProject?._id || "",
      Version: originalRowData.Version, // Use original data
      VersionDate: originalRowData.VersionDate
        ? new Date(originalRowData.VersionDate).toISOString().split("T")[0]
        : "",
    });

    setShowUpdateModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (
      !currentVersion.Project ||
      !currentVersion.Version ||
      !currentVersion.VersionDate
    ) {
      toast.error("Please fill in all fields.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
      return;
    }

    // Append time to the VersionDate
    const getCurrentTime = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      return `${hours}:${minutes}:${seconds}`;
    };

    const versionDateWithTime = `${
      currentVersion.VersionDate
    } ${getCurrentTime()}`;

    const updatedData = {
      ...currentVersion,
      VersionDate: versionDateWithTime,
    };

    try {
      await dispatch(
        updateVersion({
          id: currentVersion._id,
          versionData: updatedData,
        })
      ).unwrap();

      toast.success("Version updated successfully!");
      setShowUpdateModal(false);
      setCurrentVersion(null);
      dispatch(fetchVersions());
    } catch (error) {
      toast.error("Failed to update version.");
    }
  };

  const filteredData = VersionData?.filter((el) => {
    const matchProject =
      !filters.projectName ||
      el?.Project?.ProjectName?.toLowerCase().includes(
        filters.projectName.toLowerCase()
      );
    const matchVersion =
      !filters.version ||
      el?.Version?.toLowerCase() === filters.version.toLowerCase();

    return matchProject && matchVersion;
  });

  const columns = [
    { field: "id", headerName: "No", flex: 1 },
    { field: "Project", headerName: "Project Name", flex: 1 },
    { field: "Version", headerName: "Version", flex: 1 },
    { field: "VersionDate", headerName: "Version Date", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex space-x-2">
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => handleEditClick(params.row)}
          >
            Update
          </button>
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const rows =
    filteredData?.map((el, index) => ({
      id: index + 1,
      tokan: el?.CallNo,
      MainId: el._id,
      Project: el?.Project?.project_name,
      Version: el?.Version,
      VersionDate: formatDisplayDate(el?.VersionDate),
    })) || [];

  if (
    !VersionData ||
    !ProjectData
  ) {
    return <Loader />;
  }

  return (
    <>
      <div className="">
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            Version List
          </h3>
        </div>

        {/* Filter Section */}
        <div className="Form_bg_color bg-white shadow-md rounded-md px-4 py-4 mb-6 grid grid-cols-12 gap-4 mt-5">
          {/* Project Name Input */}
          <div className="col-span-12 lg:col-span-4 md:col-span-6">
            <h5 className="my-2 font-semibold">Search Project Name</h5>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Project Name"
              className="custom-input"
              value={filters.projectName}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  projectName: e.target.value,
                }))
              }
            />
          </div>

          {/* Version Dropdown */}
          <div className="col-span-12 lg:col-span-4 md:col-span-6">
            <h5 className="my-2 font-semibold">Search Version</h5>
            <Autocomplete
              options={versionOptions.map((ver) => ({ label: ver }))}
              getOptionLabel={(option) => option.label}
              value={filters.version ? { label: filters.version } : null}
              onChange={(e, newVal) =>
                setFilters((prev) => ({
                  ...prev,
                  version: newVal?.label || "",
                }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="custom-input"
                  placeholder="Select Version"
                  size="small"
                  fullWidth
                />
              )}
            />
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-x-auto mt-5">
          <DynamicTable columns={columns} rows={rows} />
          {/* <WpDynamicTable columns={columns} rows={rows} /> */}
        </div>

        {showUpdateModal && currentVersion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg">
              <h2 className="text-xl font-bold mb-4">Update Version</h2>
              <form onSubmit={handleUpdateSubmit} className="space-y-4">
                {/* Project dropdown */}
                <div>
                  <label className="block font-semibold">Project</label>
                  <select
                    name="Project"
                    value={currentVersion.Project}
                    onChange={(e) =>
                      setCurrentVersion((prev) => ({
                        ...prev,
                        Project: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 p-2 rounded"
                  >
                    <option value="">Select Project</option>
                    {[...(ProjectData || [])]
                      .sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""))
                      .map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.project_name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Version */}
                <div>
                  <label className="block font-semibold">Version</label>
                  <input
                    type="text"
                    name="Version"
                    value={currentVersion.Version}
                    onChange={(e) =>
                      setCurrentVersion((prev) => ({
                        ...prev,
                        Version: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 p-2 rounded"
                  />
                </div>

                {/* Version Date */}
                <div>
                  <label className="block font-semibold">Version Date</label>
                  <input
                    type="date"
                    name="VersionDate"
                    value={currentVersion.VersionDate}
                    onChange={(e) =>
                      setCurrentVersion((prev) => ({
                        ...prev,
                        VersionDate: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 p-2 rounded"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowUpdateModal(false)}
                    className="px-4 py-2 bg-gray-400 text-white rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ShowVersion;
